import { Link, useLocation } from "react-router-dom";
import { useState } from "react";
import { PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";

const RAIL_WIDTH = 68;
const PANEL_WIDTH = 248;

/**
 * Desktop navigation rail for coach/admin.
 *
 * Collapsed it is a 68px icon rail; expanded it is a 248px panel with the
 * section labels. When pinned the panel takes real layout width (content
 * reflows next to it); when unpinned it stays a rail and expands as an
 * overlay on hover, so the content underneath never jumps around.
 */
export function DesktopSidebar({ groups, pinned, onPinnedChange }) {
	const location = useLocation();
	const { t } = useLanguage();
	const [hovered, setHovered] = useState(false);

	const expanded = pinned || hovered;

	return (
		<aside
			className="hidden md:block shrink-0 relative z-40"
			style={{
				width: pinned ? PANEL_WIDTH : RAIL_WIDTH,
				transition: "width 0.24s cubic-bezier(0.22, 1, 0.36, 1)",
			}}
			data-testid="desktop-sidebar"
		>
			<div
				onMouseEnter={() => setHovered(true)}
				onMouseLeave={() => setHovered(false)}
				className="absolute inset-y-0 left-0 flex flex-col bg-card border-r border-border overflow-hidden"
				style={{
					width: expanded ? PANEL_WIDTH : RAIL_WIDTH,
					transition: "width 0.24s cubic-bezier(0.22, 1, 0.36, 1)",
					boxShadow:
						!pinned && hovered ? "8px 0 32px rgba(0, 0, 0, 0.45)" : "none",
				}}
				data-expanded={expanded ? "true" : "false"}
			>
				<nav className="flex-1 overflow-y-auto overflow-x-hidden py-3">
					{groups.map((group, groupIndex) => (
						<div key={group.id} className={groupIndex > 0 ? "mt-4" : ""}>
							<div className="h-6 px-3 flex items-center">
								{expanded ? (
									<span className="text-[10px] uppercase tracking-widest text-muted-foreground whitespace-nowrap animate-fadeIn">
										{group.label}
									</span>
								) : (
									<span className="w-full h-px bg-border" />
								)}
							</div>

							<div className="px-2 mt-1 space-y-0.5">
								{group.links.map((link) => {
									const Icon = link.icon;
									const isActive = location.pathname === link.href;
									return (
										<Link
											key={link.href}
											to={link.href}
											title={expanded ? undefined : link.label}
											aria-label={link.label}
											className={`group relative flex items-center h-10 rounded-sm transition-colors ${
												isActive
													? "bg-volt/10 text-volt"
													: "text-muted-foreground hover:text-foreground hover:bg-white/5"
											}`}
											data-testid={`nav-${link.href.slice(1)}`}
										>
											{isActive && (
												<span className="absolute left-0 top-1.5 bottom-1.5 w-[2px] rounded-full bg-volt" />
											)}

											<span className="relative w-[52px] shrink-0 flex items-center justify-center">
												<Icon className="w-[18px] h-[18px]" />
												{link.badge > 0 && !expanded && (
													<span className="absolute top-0.5 right-2.5 w-2 h-2 rounded-full bg-blaze ring-2 ring-card" />
												)}
											</span>

											<span
												className="flex-1 min-w-0 flex items-center justify-between gap-2 pr-3"
												style={{
													opacity: expanded ? 1 : 0,
													transition: "opacity 0.18s ease",
													pointerEvents: expanded ? undefined : "none",
												}}
											>
												<span className="text-sm font-medium whitespace-nowrap truncate">
													{link.label}
												</span>
												{link.badge > 0 && (
													<span className="shrink-0 h-5 min-w-[20px] px-1 flex items-center justify-center rounded-full bg-blaze text-white text-[11px] font-medium">
														{link.badge > 99 ? "99+" : link.badge}
													</span>
												)}
											</span>
										</Link>
									);
								})}
							</div>
						</div>
					))}
				</nav>

				<div className="border-t border-border p-2">
					<button
						type="button"
						onClick={() => onPinnedChange(!pinned)}
						title={pinned ? t("navUnpinSidebar") : t("navPinSidebar")}
						aria-label={pinned ? t("navUnpinSidebar") : t("navPinSidebar")}
						className="w-full flex items-center h-9 rounded-sm text-muted-foreground hover:text-foreground hover:bg-white/5 transition-colors"
						data-testid="sidebar-pin-toggle"
					>
						<span className="w-[52px] shrink-0 flex items-center justify-center">
							{pinned ? (
								<PanelLeftClose className="w-[18px] h-[18px]" />
							) : (
								<PanelLeftOpen className="w-[18px] h-[18px]" />
							)}
						</span>
						<span
							className="text-xs whitespace-nowrap"
							style={{
								opacity: expanded ? 1 : 0,
								transition: "opacity 0.18s ease",
							}}
						>
							{pinned ? t("navUnpinSidebar") : t("navPinSidebar")}
						</span>
					</button>
				</div>
			</div>
		</aside>
	);
}
